import { GlassCard } from "@/components/ui/glass-card";
import { type Device } from "@/hooks/useDevices";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Smartphone, Network, HardDrive, Monitor, Signal, User, Info } from "lucide-react";
interface SystemInformationProps {
  device: Device;
}
const SystemInformation = ({
  device
}: SystemInformationProps) => {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };
  const formatBytes = (bytes: number | null | undefined) => {
    if (!bytes) return 'Not Reported';
    return `${bytes.toFixed(2)} GB`;
  };
  const formatPercentage = (decimal: number | null | undefined) => {
    if (decimal === null || decimal === undefined) return null;
    return `${Math.round(decimal * 100)}%`;
  };
  const formatMacAddress = (mac: string | undefined) => {
    if (!mac) return 'Not Reported';
    return mac;
  };
  const formatBoolean = (value: boolean | undefined) => {
    if (value === undefined) return 'Not Reported';
    return value ? 'Yes' : 'No';
  };
  const additional = device.deviceAdditionalProperties;
  const isMac = device.os_type?.toLowerCase() === 'macos';
  const isIOS = device.os_type?.toLowerCase() === 'ios' || device.os_type?.toLowerCase() === 'ipados';
  const InfoField = ({
    label,
    value
  }: {
    label: string;
    value: string | null | undefined;
  }) => {
    const displayValue = value || 'Not Reported';
    const isLongValue = displayValue.length > 30;
    return <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        {isLongValue ? <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="font-medium text-sm text-foreground truncate cursor-help">
                  {displayValue}
                </p>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs break-all">{displayValue}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider> : <p className="font-medium text-sm text-foreground">
            {displayValue}
          </p>}
      </div>;
  };
  return <GlassCard variant="default" hover={false} className="p-6 mx-0">
      <h2 className="text-xl font-semibold mb-4 text-foreground">System Information</h2>
      
      <Accordion type="multiple" defaultValue={["identity", "network", "hardware", "system"]} className="w-full">
        {/* Device Identity */}
        <AccordionItem value="identity">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-primary" />
              <span className="font-semibold">Device Identity</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 pl-4">
              <InfoField label="Device Name" value={device.name} />
              <InfoField label="Hostname" value={device.hostname} />
              <InfoField label="Serial Number" value={device.serial_number} />
              <InfoField label="UDID" value={additional?.UDID} />
              <InfoField label="Product" value={additional?.ProductName} />
              {additional?.ModelNumber && <InfoField label="Model Number" value={additional.ModelNumber} />}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Network Information */}
        <AccordionItem value="network">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <Network className="h-4 w-4 text-primary" />
              <span className="font-semibold">Network Information</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 pl-4">
              <InfoField label="IP Address" value={device.ip_address} />
              <InfoField label="WiFi MAC Address" value={formatMacAddress(additional?.WiFiMAC)} />
              <InfoField label="Bluetooth MAC Address" value={formatMacAddress(additional?.BluetoothMAC)} />
              {isMac && additional?.EthernetMAC && <InfoField label="Ethernet MAC Address" value={formatMacAddress(additional.EthernetMAC)} />}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Hardware & Storage */}
        <AccordionItem value="hardware">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-primary" />
              <span className="font-semibold">Hardware & Storage</span>
            </div>
          </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 pl-4">
              <InfoField label="Battery Level" value={formatPercentage(additional?.BatteryLevel || device.battery_level)} />
              <InfoField label="Battery Status" value={device.battery_status} />
              <InfoField label="Total Capacity" value={formatBytes(additional?.DeviceCapacity)} />
              <InfoField label="Available Capacity" value={formatBytes(additional?.AvailableDeviceCapacity)} />
              {additional?.HasBattery !== undefined && <InfoField label="Has Battery" value={formatBoolean(additional.HasBattery)} />}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* System Details */}
        <AccordionItem value="system">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <Monitor className="h-4 w-4 text-primary" />
              <span className="font-semibold">System Details</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 pl-4">
              <InfoField label="Operating System" value={device.os_type} />
              <InfoField label="OS Version" value={device.os_version} />
              <InfoField label="Build Version" value={additional?.BuildVersion} />
              <InfoField label="Hostname" value={device.hostname} />
              {additional?.SupplementalBuildVersion && <InfoField label="Supplemental Build" value={additional.SupplementalBuildVersion} />}
              {isIOS && additional?.TimeZone && <InfoField label="Time Zone" value={additional.TimeZone} />}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Cellular Information (iOS only) */}
        {isIOS && additional?.ServiceSubscriptions && additional.ServiceSubscriptions.length > 0 && <AccordionItem value="cellular">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <Signal className="h-4 w-4 text-primary" />
                <span className="font-semibold">Cellular Information</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pt-2 pl-4">
                {additional.ServiceSubscriptions.map((sub, index) => <div key={index} className="border-b border-border/30 pb-4 last:border-0">
                    <p className="text-xs font-semibold text-muted-foreground mb-2">
                      {sub.Slot?.replace('CTSubscriptionSlot', 'Slot ')}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {sub.IMEI && <InfoField label="IMEI" value={sub.IMEI} />}
                      {sub.MEID && <InfoField label="MEID" value={sub.MEID} />}
                      {sub.EID && <InfoField label="EID" value={sub.EID} />}
                    </div>
                  </div>)}
                {additional.ModemFirmwareVersion && <InfoField label="Modem Firmware" value={additional.ModemFirmwareVersion} />}
              </div>
            </AccordionContent>
          </AccordionItem>}

        {/* User Information (Mac only) */}
        {isMac && (additional?.UserLongName || additional?.UserShortName || additional?.UserID) && <AccordionItem value="user">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-primary" />
                <span className="font-semibold">User Information</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 pl-4">
                <InfoField label="User ID" value={additional?.UserID} />
                <InfoField label="User Name" value={additional?.UserLongName} />
                <InfoField label="Short Name" value={additional?.UserShortName} />
                <InfoField label="On Console" value={formatBoolean(!additional?.NotOnConsole)} />
              </div>
            </AccordionContent>
          </AccordionItem>}

        {/* Status & Policies */}
        <AccordionItem value="status">
          <AccordionTrigger className="hover:no-underline">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-primary" />
              <span className="font-semibold">General Information</span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-2 pl-4">
              <InfoField label="Last Seen" value={formatDate(device.last_seen || device.created_at)} />
              <InfoField label="Supervised" value={formatBoolean(additional?.IsSupervised)} />
              {isIOS && <>
                  <InfoField label="Activation Lock" value={formatBoolean(additional?.IsActivationLockEnabled)} />
                  <InfoField label="Cloud Backup" value={formatBoolean(additional?.IsCloudBackupEnabled)} />
                  <InfoField label="Lost Mode" value={formatBoolean(additional?.IsMDMLostModeEnabled)} />
                  <InfoField label="Data Roaming" value={formatBoolean(additional?.DataRoamingEnabled)} />
                  <InfoField label="Personal Hotspot" value={formatBoolean(additional?.PersonalHotspotEnabled)} />
                  <InfoField label="Find My Device" value={formatBoolean(additional?.IsDeviceLocatorServiceEnabled)} />
                </>}
              <InfoField label="iTunes Account Active" value={formatBoolean(additional?.iTunesStoreAccountIsActive)} />
              <InfoField label="Awaiting Configuration" value={formatBoolean(additional?.AwaitingConfiguration)} />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </GlassCard>;
};
export default SystemInformation;